/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {TypeaheadHelperService} from '../../../../shared/services/typeahead-helper.service';
import {combineLatest, Observable, of, timer} from 'rxjs';
import {MandatoryMessageTypeEnum} from '../../../../shared/input/service/mandatory-message-type.enum';
import {InputMessagesService} from '../../../../shared/input/service/input-messages.service';
import {InputDropdownComponent} from '../../../../shared/input/input-dropdown/input-dropdown.component';
import {CountryModel} from '../../../../api/ms-settings/business/schema/v1/contracts/country.model';
import {BusinessSettingsApi} from '../../../../api/ms-settings/business/schema/v1/business-settings.api';
import {take} from 'rxjs/operators';
import {EntityElementModel} from '../../../../shared/entity-tree/entity-element/models/entity-element.model';
import {BusinessSettingsModal} from '../business-settings-modal';
import {WarningsEnum} from '../../../../shared/warnings/warnings.enum';
import {BusinessSettingsModalState} from '../business-settings-modal.state';
import {InputListModel} from '../input-list.model';
import {InputTypeEnum} from '../../../../shared/input/input-type.enum';

@Component({
    selector: 'app-country-modal',
    templateUrl: './country-modal.component.html',
    styleUrls: ['../business-settings-modal.css', './country-modal.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CountryModalComponent extends BusinessSettingsModal<BusinessSettingsModalState<CountryModel>> {
    @ViewChild('country') countryComponent: InputDropdownComponent;
    public countryInputMessages = new Map<WarningsEnum, string[]>();

    readonly ENTITY_NAME = 'country/region';
    readonly EDIT_TITLE = `Edit ${this.ENTITY_NAME}`;
    readonly ADD_TITLE = `Add a ${this.ENTITY_NAME}`;
    readonly EDIT_SUBMIT_TITLE = 'Edit';
    readonly ADD_SUBMIT_TITLE = 'Add';
    private countriesList: CountryModel[];

    constructor(public typeahead: TypeaheadHelperService,
                private inputMessagesService: InputMessagesService, private settingsApi: BusinessSettingsApi) {
        super(inputMessagesService);
    }

    getInitialData(): void {
        this.settingsApi.getAllCountriesOrderedByName()
                .pipe(take(1))
                .subscribe((countries: CountryModel[]) => {
                    this.countriesList = countries.filter((country) => {
                        if (this.excludedEntities != null) {
                            return !this.excludedEntities.map((entity) => entity.code)
                                    .includes(country.alpha2Code);
                        }
                    });
                });
    }

    setEntity(element: EntityElementModel): void {
        this.storeState({
            originalElement: element,
            originalValue: element.originalValue
        });
        combineLatest([this.settingsApi.getAllCountriesOrderedByName(), this.onReady])
                .pipe(take(1))
                .subscribe(([countries, isReady]: [CountryModel[], boolean]) => {
                    timer(0)
                            .subscribe(() => {
                                this.storeState({
                                    currentValue: countries
                                            .find((country) => country.alpha2Code.toLowerCase() === element.code.toLowerCase())
                                });
                                this.countryComponent.setValue(this.state.currentValue);
                                this.setActiveInputState(element.active, true);
                            });
                });
    }

    /**
     * Filter countries list (autocomplete)
     */
    searchCountries = (term: string): Observable<any> => {
        if (term != null && term.length > 0) {
            return of(this.countriesList.filter((value) => `${value.name}`.toLowerCase()
                    .indexOf(term.toLowerCase()) > -1));

        }
        return of(this.countriesList);
    };

    countryInputChange(country: CountryModel): void {
        this.inputMessagesService.mandatoryWarnings(this.countryComponent.inputControl,
                this.countryInputMessages,
                MandatoryMessageTypeEnum.SAVE_AND_SEND);
        this.inputMessagesService.invalidInputValueError(this.countryComponent.inputControl,
                this.countryInputMessages,
                this.countryComponent.label);
        this.countryInputMessages = new Map(this.countryInputMessages);

        if (country == null || country.constructor === ''.constructor) {
            this.storeState({
                currentValue: null,
                invalid: {...this.state.invalid, country: true}
            });
        } else {
            this.storeState({
                currentValue: {...country, code: country.alpha2Code},
                invalid: {...this.state.invalid, country: false}
            });
        }

        this.setModalValidity();
    }

    protected generateInputs(): InputListModel[] {
        return [{input: this.countryComponent, type: InputTypeEnum.DROPDOWN, mandatory: true}];
    }
}
